import { ChevronRight, Shield, Zap, Scale, Wind, Star } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { useIsMobile } from "@/hooks/use-mobile";

const Hero = () => {
  const isMobile = useIsMobile();
  
  const scrollToDemo = () => {
    const demoSection = document.getElementById('demo-section');
    if (demoSection) {
      window.scrollTo({
        top: demoSection.offsetTop - 80,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section className="relative pt-24 md:pt-32 pb-16 md:pb-24 overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0 bg-hero-pattern z-0"></div>
      <div className="absolute top-20 right-0 w-96 h-96 bg-wamy-200 rounded-full filter blur-3xl opacity-20 animate-pulse-subtle"></div>
      <div className="absolute bottom-20 left-10 w-96 h-96 bg-teal-200 rounded-full filter blur-3xl opacity-20 animate-pulse-subtle"></div>
      
      <div className="relative z-10 max-w-full mx-auto px-4 sm:px-6 lg:max-w-7xl lg:px-8">
        <div className="flex flex-col lg:flex-row items-center">
          <div className="lg:w-1/2 lg:pr-12 w-full">
            <div className="text-center lg:text-left">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight opacity-0 animate-fade-in mb-6">
                Claim Intelligence Platform with <span className="text-gradient">AI Legal Workers</span>
              </h1>
              
              <p className="text-xl text-gray-600 opacity-0 animate-fade-in-delay-2 max-w-xl mx-auto lg:mx-0 mb-8">
                Collect evidence, refine data, score risk, uncover gaps, and empower AI legal workers to craft winning strategies - all in one seamless workspace.
              </p>
              
              <div className="flex justify-center lg:justify-start opacity-0 animate-fade-in-delay-3">
                <Button 
                  size="lg"
                  className="bg-gradient-to-r from-wamy-600 to-teal-500 hover:from-wamy-700 hover:to-teal-600 text-white shadow-md hover:shadow-lg transition-all"
                  onClick={scrollToDemo}
                >
                  Request Demo
                  <ChevronRight className="ml-2 w-4 h-4" />
                </Button>
              </div>
            </div>
            
            <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-6 opacity-0 animate-fade-in-delay-3">
              <div className="flex items-center">
                <Zap className="w-5 h-5 text-wamy-600 mr-2" />
                <span className="text-sm text-gray-600">Automated Data Collection</span>
              </div>
              <div className="flex items-center">
                <Shield className="w-5 h-5 text-wamy-600 mr-2" />
                <span className="text-sm text-gray-600">Risk Assessment & Analytics</span>
              </div>
              <div className="flex items-center">
                <Scale className="w-5 h-5 text-wamy-600 mr-2" />
                <span className="text-sm text-gray-600">Dynamic Scoring & Collaboration</span>
              </div>
            </div>
          </div>
          
          <div className="lg:w-1/2 mt-12 lg:mt-0 opacity-0 animate-fade-in-delay-2 w-full">
            <div className="relative mx-auto w-full max-w-md lg:max-w-lg">
              <div className="relative backdrop-blur-sm bg-white/70 rounded-2xl p-4 sm:p-6 shadow-xl border border-gray-100 overflow-hidden transition-all duration-500 hover:shadow-2xl group">
                <div className="absolute inset-0 bg-gradient-to-tr from-wamy-100/50 to-teal-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                
                <div className="relative">
                  <div className="bg-white rounded-xl overflow-hidden shadow-md">
                    <div className="bg-gradient-to-r from-wamy-600 to-teal-500 py-3 px-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="h-2.5 w-2.5 rounded-full bg-red-400 mr-2"></div>
                          <div className="h-2.5 w-2.5 rounded-full bg-yellow-400 mr-2"></div>
                          <div className="h-2.5 w-2.5 rounded-full bg-green-400"></div>
                        </div>
                        <div className="text-xs text-white font-medium">Property Claim #23587</div>
                      </div>
                    </div>
                    
                    <div className="p-4">
                      <div className="flex items-center justify-between mb-4">
                        <div className="text-lg font-semibold text-gray-800">Wind Damage Assessment</div>
                        <Badge className="bg-green-100 text-green-800 hover:bg-green-200">
                          <Star className="w-3 h-3 mr-1" fill="currentColor" />
                          84/100
                        </Badge>
                      </div>
                      
                      <div className="flex flex-wrap gap-2 text-xs text-gray-600 mb-4">
                        <span className="px-2 py-1 bg-blue-50 rounded-md">Residential</span>
                        <span className="px-2 py-1 bg-blue-50 rounded-md">Wind Damage</span>
                        <span className="px-2 py-1 bg-green-50 rounded-md">Low Risk</span>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div className="bg-gray-50 rounded-lg p-3">
                          <div className="text-xs text-gray-500 mb-1">Estimated Damage</div>
                          <div className="font-semibold text-wamy-700">$24,580.00</div>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3">
                          <div className="text-xs text-gray-500 mb-1">Date Filed</div>
                          <div className="font-semibold text-wamy-700">Aug 12, 2023</div>
                        </div>
                      </div>
                      
                      <div className="space-y-3 mb-4">
                        <div>
                          <div className="flex justify-between text-xs mb-1">
                            <span className="text-gray-500">Document Analysis</span>
                            <span className="text-wamy-700 font-medium">98%</span>
                          </div>
                          <Progress value={98} className="h-1.5 bg-gray-100" />
                        </div>
                        <div>
                          <div className="flex justify-between text-xs mb-1">
                            <span className="text-gray-500">Risk Assessment</span>
                            <span className="text-wamy-700 font-medium">84%</span>
                          </div>
                          <Progress value={84} className="h-1.5 bg-gray-100" />
                        </div>
                        <div>
                          <div className="flex justify-between text-xs mb-1">
                            <span className="text-gray-500">Coverage Verification</span>
                            <span className="text-wamy-700 font-medium">100%</span>
                          </div>
                          <Progress value={100} className="h-1.5 bg-gray-100" />
                        </div>
                      </div>
                      
                      <div className="bg-blue-50 rounded-lg p-3 text-sm text-blue-800">
                        <div className="font-medium mb-1">AI Insights</div>
                        <p className="text-xs">Assessment score of 84/100 indicates low risk profile. Verified documentation and damage patterns consistent with reported wind event. Recommended for standard processing.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Floating notifications - repositioned for mobile */}
              {!isMobile ? (
                <>
                  <div className="absolute top-0 right-0 sm:-right-20 bg-white/90 backdrop-blur-sm border border-gray-100 shadow-md rounded-lg p-3 animate-float">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-green-100 text-green-600 rounded-full flex items-center justify-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-800">Risk assessment completed</p>
                        <p className="text-xs text-gray-500">3 minutes ago</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="absolute -bottom-10 left-0 sm:-left-10 bg-white/90 backdrop-blur-sm border border-gray-100 shadow-md rounded-lg p-3 animate-float" style={{ animationDelay: '2s' }}>
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-wamy-100 text-wamy-600 rounded-full flex items-center justify-center">
                        <Wind className="h-4 w-4" />
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-800">Wind data analysis complete</p>
                        <p className="text-xs text-gray-500">Just now</p>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="mt-6 space-y-3 w-full">
                  <div className="bg-white/90 backdrop-blur-sm border border-gray-100 shadow-md rounded-lg p-3">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-green-100 text-green-600 rounded-full flex items-center justify-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-800">Risk assessment completed</p>
                        <p className="text-xs text-gray-500">3 minutes ago</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-white/90 backdrop-blur-sm border border-gray-100 shadow-md rounded-lg p-3">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-wamy-100 text-wamy-600 rounded-full flex items-center justify-center">
                        <Wind className="h-4 w-4" />
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-800">Wind data analysis complete</p>
                        <p className="text-xs text-gray-500">Just now</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
